import React, {useState, useEffect} from 'react';
import { useFormik } from 'formik'
import * as Yup from 'yup';
import dayjs from 'dayjs';
import AppBar from "../components/app_bar/AppBar";
import Drawer from "../components/Drawer";
import { useLocation } from "react-router-dom";
import { 
    Checkbox, useMediaQuery, Toolbar, Box, CssBaseline, Grid, Typography,
    Autocomplete, TextField, Select, MenuItem, Button, Chip, Stack,
    ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useParams, useNavigate } from "react-router-dom";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import RemoveIcon from "@mui/icons-material/RemoveCircleOutlineSharp";
import SendIcon from '@mui/icons-material/Send';
import * as CONSTANTS from "../constants"

import { 
    view_wazaren_namelist,
    view_organization_namelist,
} from "../services/topic_view";

import { 
    preview_wotageishi, 
    save_preview_wotageishi, 
    approve_wotageishi, 
    exit_preview_wotageishi,
    heartbeat_preview_wotageishi, 
} from '../services/topic_approve';

const WotageishiApprovePreview = () => {

    document.title = `Approve Wotageishis (Preview) - Wotagei Zone`

    const navigate = useNavigate();
    const [previewWotageishi, setPreviewWotageishi] = useState(null);

    const formik = useFormik({
        initialValues: {
            name_en: '',
            name_jp: '',
            name_tc: '',
            name_sc: '',
            default_name: '',
            default_language: 'jp',
            active_regions: [],
            markings: [],
            official_account_youtube: '',
            official_account_bilibili: '',
            official_account_x: '',
            organization_uids: [],
            organization_names: [],
            original_wazaren_uids: [],
            original_wazaren_names: [],
            has_year: true,
            has_month: true,
            has_day: true,
            wotageishi_uid: 0,
        },
        validationSchema: Yup.object({
            name_en: Yup.string(),
            name_jp: Yup.string(),
            name_tc: Yup.string(),
            name_sc: Yup.string(),
            default_name: Yup.string(),
            default_language: Yup.string(),
            active_regions: Yup.array().of(Yup.string()), 
            markings: Yup.array().of(Yup.string()), // pending fill-in
            official_account_youtube: Yup.string(),
            official_account_bilibili: Yup.string(),
            official_account_x: Yup.string(),
            organization_uids: Yup.array().of(Yup.number()), 
            organization_names: Yup.array().of(Yup.string()),
            original_wazaren_uids: Yup.array().of(Yup.number()),
            original_wazaren_names: Yup.array().of(Yup.string()),
            start_date: Yup.object().nullable().dayjs(),
            has_year: Yup.bool(),
            has_month: Yup.bool(),
            has_day: Yup.bool(),
            wotageishi_uid: Yup.number(),
        }),
        onSubmit: (values) => {
            if(!values.name_en && !values.name_jp && !values.name_sc && !values.name_tc) {
                formik.setFieldError('name_en', CONSTANTS.REQUEST_TOPIC.ERROR_MESSAGE.REQUIRED_NAME.EN[lang])
                formik.setFieldError('name_jp', CONSTANTS.REQUEST_TOPIC.ERROR_MESSAGE.REQUIRED_NAME.JP[lang])
                formik.setFieldError('name_tc', CONSTANTS.REQUEST_TOPIC.ERROR_MESSAGE.REQUIRED_NAME.TC[lang])
                formik.setFieldError('name_sc', CONSTANTS.REQUEST_TOPIC.ERROR_MESSAGE.REQUIRED_NAME.SC[lang])
                return
            }
            if(!values.default_name) {
                switch (values.default_language) {
                    case 'en':
                    case 'jp':
                    case 'tc':
                    case 'sc':
                        formik.setFieldError('name_'+values.default_language, CONSTANTS.REQUEST_TOPIC.ERROR_MESSAGE.NON_EMPTY_DEFAULT_NAME[lang]);
                        return;
                    default:
                        return;
                }
            }
        },
    });

    let {lang, temp_id} = useParams();
    const heartbeatInterval = 15000; // 15 seconds
    const [drawerOpen, setDrawerOpen] = useState(false);

    const handleDrawerOpen = (open) => {
        if (open === undefined) {
            setDrawerOpen(!drawerOpen)
        } else {
            setDrawerOpen(open);
        }
    }

    const location = useLocation();
    const fetchPreviewWotageishi = async () => {
        const [status, result] = await preview_wotageishi(temp_id, lang);
        if (status === 200) {
            if (Object.keys(result).length == 0) {
                console.log(window.history.length)
                window.alert("Someone is editing this page. Redirecting to previous page.");
                if (window.history.length > 1) {
                    window.history.go(-1);
                } else {
                    window.close();
                }
            } else {
                setPreviewWotageishi(result);
                autoFillFormik(result.reviewed_content)
            }
        }
    }

    // send api request for url changes
    useEffect(() => {
        fetchPreviewWotageishi();
        fetchWazarens('');
        fetchOrganizations('');
    }, [location.pathname]);

    useEffect(() => {
        if (previewWotageishi === null) return;
        const sendHeartbeat = async () => {
            await heartbeat_preview_wotageishi(temp_id)
        }
        const intervalId = setInterval(sendHeartbeat, heartbeatInterval);
        window.addEventListener('beforeunload', () => {
            exit_preview_wotageishi(temp_id);
        });
        return () => {
            clearInterval(intervalId);
            document.removeEventListener('beforeunload', exit_preview_wotageishi(temp_id));
        };
    }, [previewWotageishi]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));  // Mobile detection

    const [orgWazarens, setOrgWazarens] = useState([]);
    const [wazarens, setWazarens] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrganizations, setSelectedOrganizations] = useState([]);
    const [countries, setCountries] = useState([]);
    const [marking, setMarking] = useState('');
    const [markings, setMarkings] = useState(formik.values.markings);
    const [countryList, setCountryList] = useState(CONSTANTS.COUNTRY_LIST);
    

    const updateOrgWazarens = (wazarens) => {
        setOrgWazarens(wazarens);
        let original_wazaren_uids = [];
        let original_wazaren_names = [];
        wazarens.forEach(wazaren => {
            original_wazaren_uids.push(wazaren.uid);
            original_wazaren_names.push(wazaren.name)
        })
        formik.values.original_wazaren_uids = original_wazaren_uids;
        formik.values.original_wazaren_names = original_wazaren_names;
    }

    const updateSelectedOrganizations = (organizations) => {
        setSelectedOrganizations(organizations);
        let organization_uids = [];
        let organization_names = [];
        organizations.forEach(organization => {
            organization_uids.push(organization.uid);
            organization_names.push(organization.name)
        })
        formik.values.organization_uids = organization_uids;
        formik.values.organization_names = organization_names;
    }

    function filterCountries(countries, letter) {
        letter = letter.toLowerCase();
        var filteredCountries = countries.filter(function(country) {
            return country.label[lang].toLowerCase().startsWith(letter);
        });
        return filteredCountries;
    }

    const updateCountries = (countries) => {
        setCountries(countries);
        let active_regions = []
        countries.forEach(country => {
            active_regions.push(country.code)
        })
        formik.values.active_regions = active_regions;
    }
    const updateMarkings = (markings) => {
        setMarkings(markings);
        formik.values.markings = markings;
        setMarking('');
    }

    const updateWazarenTextField = (newValue) => {
        fetchWazarens(newValue);
    }
    const updateOrganizationTextField = (newValue) => {
        fetchOrganizations(newValue);
    }

    const fetchWazarens = async (name) => {
            const [status, result] = await view_wazaren_namelist(lang, name)
            if (status === 200) {
                setWazarens(result.wazarens);
            }
        };
    
    const fetchOrganizations = async (name) => {
        const [status, result] = await view_organization_namelist(lang, name)
        if (status === 200) {
            setOrganizations(result.organizations);
        }
    };

    const UpdateDefaultName = (option) => {
        switch (option) {
            case 'en':
                formik.values.default_name = formik.values.name_en;
                break;
            case 'jp':
                formik.values.default_name = formik.values.name_jp;
                break;
            case 'tc':
                formik.values.default_name = formik.values.name_tc;
                break;
            case 'sc':
                formik.values.default_name = formik.values.name_sc;
                break;
        }
    }

    const radioButtonMap = {
        'en': CONSTANTS.REQUEST_TOPIC.FIELDS.LANG.EN[lang], 
        'jp': CONSTANTS.REQUEST_TOPIC.FIELDS.LANG.JP[lang], 
        'tc': CONSTANTS.REQUEST_TOPIC.FIELDS.LANG.TC[lang],
        'sc': CONSTANTS.REQUEST_TOPIC.FIELDS.LANG.SC[lang], 
    };
    
    let tagKeys = []
    for (let index = 1; index <= Object.keys(CONSTANTS.WAZAREN_TAGS).length; index++) {
        tagKeys.push(index);
    }

    const autoFillFormik = (currentWotageishi) => {
        if (currentWotageishi === null) return
        
        // Update formik values one by one
        formik.values.name_en = currentWotageishi.name.en;
        formik.values.name_jp = currentWotageishi.name.jp;
        formik.values.name_tc = currentWotageishi.name.tc;
        formik.values.name_sc = currentWotageishi.name.sc;
        formik.values.default_name = currentWotageishi.default_name;
        formik.values.active_regions = currentWotageishi.active_regions;
        formik.values.markings = currentWotageishi.markings;
        formik.values.official_account_youtube = currentWotageishi.official_account.youtube;
        formik.values.official_account_bilibili = currentWotageishi.official_account.bilibili;
        formik.values.official_account_x = currentWotageishi.official_account.x;
        
        let organization_names = [];
        let organization_uids = [];
        currentWotageishi.organizations.map((organization, index) => {
            organization_names.push(organization.name);
            organization_uids.push(organization.uid)
        })
        formik.values.organization_uids = organization_uids;
        formik.values.organization_names = organization_names;

        let original_wazaren_uids = [];
        let original_wazaren_names = [];
        currentWotageishi.original_wazarens.map((original_wazaren, index) => {
            original_wazaren_uids.push(original_wazaren.uid);
            original_wazaren_names.push(original_wazaren.name);
        })
        formik.values.original_wazaren_uids = original_wazaren_uids;
        formik.values.original_wazaren_names = original_wazaren_names;
        
        formik.values.start_date = dayjs(currentWotageishi.start_date.value);
        formik.values.has_year = currentWotageishi.start_date.has_year;
        formik.values.has_month = currentWotageishi.start_date.has_month;
        formik.values.has_day = currentWotageishi.start_date.has_day;
        setMarkings(currentWotageishi.markings);
    }

    const insertOrgWazarens = (original_wazaren_uids, original_wazaren_names) => {
        const orgWazarens_v = original_wazaren_uids.map((original_wazaren_uid, index) => ({
            uid: original_wazaren_uid,
            name: original_wazaren_names[index]
        }));
        setOrgWazarens(orgWazarens_v);
    };

    const insertCountries = (active_regions) => {
        const countries_v = active_regions.map((countryCode, index) => (countryList.find(o => o.code===countryCode)));
        setCountries(countries_v);
    };

    const insertOrganizations = (organization_uids, organization_names) => {
        const organizations_v = organization_uids.map((organization_uid, index) => ({
            uid: organization_uid,
            name: organization_names[index]
        }));
        setSelectedOrganizations(organizations_v);
    }

    useEffect(()=>{
        insertOrgWazarens(
            formik.values.original_wazaren_uids,
            formik.values.original_wazaren_names,
        )
    }, [formik.values.organization_names])

    useEffect(()=>{
        insertCountries(
            formik.values.active_regions
        )
    }, [formik.values.active_regions])

    useEffect(()=>{
        insertOrganizations(
            formik.values.organization_uids,
            formik.values.organization_names
        )
    }, [formik.values.organization_names])

    const returnEvent = () => {
        navigate(`/${lang}/approve-topic/wotageishi/`)
    }

    const saveEvent = async () => {
        const data = { reviewed_content: {
            name: {
                en: formik.values.name_en,
                jp: formik.values.name_jp,
                tc: formik.values.name_tc,
                sc: formik.values.name_sc,
            },
            default_name: formik.values.default_name,
            start_date: {
                value: formik.values.start_date? formik.values.start_date.toISOString(): '',
                has_year: formik.values.has_year,
                has_month: formik.values.has_month,
                has_day: formik.values.has_day,
            },
            active_regions: formik.values.active_regions,
            original_wazaren_uids: formik.values.original_wazaren_uids,
            organization_uids: formik.values.organization_uids,
            markings: formik.values.markings,
            official_account: {
                youtube: formik.values.official_account_youtube,
                bilibili: formik.values.official_account_bilibili,
                x: formik.values.official_account_x
            },
        }}
        const [status, result] = await save_preview_wotageishi(temp_id, data);
    }

    const approveEvent = async () => {
        await saveEvent();
        const [status, result] = await approve_wotageishi('approve', temp_id);
        returnEvent();
    }

    const rejectEvent = async () => {
        const [status, result] = await approve_wotageishi('reject', temp_id);
        returnEvent();
    }

    return (
        <Box >
            <CssBaseline />
            <AppBar lang={lang} handleDrawerOpen={handleDrawerOpen}/>
            <Box sx={{ display: 'flex' }}>
                <Drawer lang={lang} drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} />
                <Box component="main" sx={{ flexGrow: 1, p: isMobile? 2: 3}}>
                    <Toolbar />
                    <Grid container spacing={2}>
                        <Grid xs={12} item>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: 2,
                                    backgroundColor: "#f5f5f5", // Light background color for the bar
                                    boxShadow: 1,
                                }}
                            >
                                 <Stack direction={isMobile?'column':'row'} spacing={2}>
                                    <Button
                                        variant="contained"
                                        onClick={()=> {returnEvent()}}
                                        sx={{ backgroundColor: 'grey' }}
                                    >
                                        {CONSTANTS.APPROVE_TOPIC.COMMON.BUTTONS.RETURN[lang]}
                                    </Button>
                                    {/* Save Button */}
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={()=> {saveEvent()}}
                                    >
                                        {CONSTANTS.APPROVE_TOPIC.COMMON.BUTTONS.SAVE[lang]}
                                    </Button>
                                </Stack>

                                {/* Approved & Rejected Buttons */}
                                <Stack direction={isMobile?'column':'row'} spacing={2}>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={()=> {approveEvent()}}
                                    >
                                        {CONSTANTS.APPROVE_TOPIC.COMMON.BUTTONS.APPROVE[lang]}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={()=> {rejectEvent()}}
                                    >
                                        {CONSTANTS.APPROVE_TOPIC.COMMON.BUTTONS.REJECT[lang]}
                                    </Button>
                                </Stack>
                            </Box>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            justifyContent={'left'}
                        >
                            <Typography
                                backgroundColor="#7e7e7e"
                                color="white"
                                fontWeight="bold"
                                p={2}
                            >
                                {CONSTANTS.APPROVE_TOPIC.COMMON.FIELDS.METADATA[lang].toUpperCase()}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={4}
                            alignContent={'center'}
                        >   
                            <Typography p={2}>{CONSTANTS.APPROVE_TOPIC.COMMON.TABLE_HEADERS.REQUEST_TYPE[lang]}</Typography>
                        </Grid>
                        <Grid
                            item
                            xs={8}
                            sm={8}
                            alignContent={'center'}
                        >
                            <Typography
                                textAlign="left"
                            >
                                {
                                    previewWotageishi!==null? 
                                    (
                                        previewWotageishi.request_type === "new"?
                                        CONSTANTS.APPROVE_TOPIC.COMMON.ITEM.NEW[lang]: CONSTANTS.APPROVE_TOPIC.COMMON.ITEM.UPDATE[lang]
                                    )
                                    : '---'
                                }
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={4}
                            alignContent={'center'}
                        >   
                            <Typography p={2}>Temp ID</Typography>
                        </Grid>
                        <Grid
                            item
                            xs={8}
                            sm={8}
                            alignContent={'center'}
                        >
                            <Typography
                                textAlign="left"
                            >
                                {temp_id}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={4}
                            alignContent={'center'}
                        >   
                            <Typography p={2}>{CONSTANTS.REQUEST_TOPIC.FIELDS.WOTAGEISHI_UID[lang]}</Typography>
                        </Grid>
                        <Grid
                            item
                            xs={8}
                            sm={8}
                            alignContent={'center'}
                        >
                            <Typography
                                textAlign="left"
                            >
                                {previewWotageishi!==null && previewWotageishi.existing_uid!==0? previewWotageishi.existing_uid: '---'}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            justifyContent={'left'}
                        >
                            <Typography
                                backgroundColor="#7e7e7e"
                                color="white"
                                fontWeight="bold"
                                p={2}
                            >
                                {CONSTANTS.REQUEST_TOPIC.FIELDS.NAME[lang]}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={4}
                            alignContent={'center'}
                        >   
                            <Typography p={2}>
                                {CONSTANTS.REQUEST_TOPIC.FIELDS.LANG.EN[lang]}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={8}
                            alignContent={'center'}
                            pr={2}
                        >   
                            <TextField
                                name="English Name"
                                variant="outlined"
                                placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.NAME.EN[lang]}
                                type="text"
                                fullWidth
                                size="small"
                                error={Boolean(formik.touched.name_en && formik.errors.name_en)}
                                helperText={formik.errors.name_en && formik.errors.name_en}
                                onChange={(e) => {
                                    formik.handleChange("name_en")(e);
                                    if (formik.values.default_language === 'en') {
                                        formik.handleChange("default_name")(e);
                                    }
                                }}
                                value={formik.values.name_en}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={4}
                            alignContent={'center'}
                        >   
                            <Typography p={2}>{CONSTANTS.REQUEST_TOPIC.FIELDS.LANG.JP[lang]}</Typography>
                        </Grid>
                        <Grid
                            item
                            xs={8}
                            alignContent={'center'}
                            pr={2}
                        >   
                            <TextField
                                name="Japanese Name"
                                variant="outlined"
                                placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.NAME.JP[lang]}
                                type="text"
                                fullWidth
                                size="small"
                                error={Boolean(formik.touched.name_jp && formik.errors.name_jp)}
                                helperText={formik.errors.name_jp && formik.errors.name_jp}
                                onChange={(e) => {
                                    formik.handleChange("name_jp")(e);
                                    if (formik.values.default_language === 'jp') {
                                        formik.handleChange("default_name")(e);
                                    }
                                }}
                                value={formik.values.name_jp}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={4}
                            alignContent={'center'}
                        >   
                            <Typography p={2}>{CONSTANTS.REQUEST_TOPIC.FIELDS.LANG.TC[lang]}</Typography>
                        </Grid>
                        <Grid
                            item
                            xs={8}
                            alignContent={'center'}
                            pr={2}
                        >   
                            <TextField
                                name="Traditional Chinese Name"
                                variant="outlined"
                                placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.NAME.TC[lang]}
                                type="text"
                                fullWidth
                                size="small"
                                error={Boolean(formik.touched.name_tc && formik.errors.name_tc)}
                                helperText={formik.errors.name_tc && formik.errors.name_tc}
                                onChange={(e) => {
                                    formik.handleChange("name_tc")(e);
                                    if (formik.values.default_language === 'tc') {
                                        formik.handleChange("default_name")(e);
                                    }
                                }}
                                value={formik.values.name_tc}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={4}
                            alignContent={'center'}
                        >   
                            <Typography p={2}>{CONSTANTS.REQUEST_TOPIC.FIELDS.LANG.SC[lang]}</Typography>
                        </Grid>
                        <Grid
                            item
                            xs={8}
                            alignContent={'center'}
                            pr={2}
                        >   
                            <TextField
                                name="Simplified Chinese Name"
                                variant="outlined"
                                placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.NAME.SC[lang]}
                                type="text"
                                fullWidth
                                size="small"
                                error={Boolean(formik.touched.name_sc && formik.errors.name_sc)}
                                helperText={formik.errors.name_sc && formik.errors.name_sc}
                                onChange={(e) => {
                                    formik.handleChange("name_sc")(e);
                                    if (formik.values.default_language === 'sc') {
                                        formik.handleChange("default_name")(e);
                                    }
                                }}
                                value={formik.values.name_sc}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={4}
                            alignContent={'center'}
                        >   
                            <Typography p={2}>{CONSTANTS.REQUEST_TOPIC.FIELDS.DEFAULT_LANG[lang]}</Typography>
                        </Grid>
                        <Grid
                            item
                            xs={8}
                            alignContent={'center'}
                            pr={2}
                        >   
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }} >
                                {
                                    isMobile?
                                    <Select
                                        defaultValue="jp"
                                        value={formik.values.default_language}
                                        onChange={(event) => {
                                            UpdateDefaultName(event.target.value);
                                            formik.setFieldValue('default_language', event.target.value);
                                        }}
                                        sx={{width: "100%"}}
                                    >
                                        {Object.keys(radioButtonMap).map((key, index) => (
                                            <MenuItem
                                                value={key}
                                            >
                                                {radioButtonMap[key]}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    :
                                    <ToggleButtonGroup
                                        orientation="horizontal"
                                        color="primary"
                                        value={formik.values.default_language}
                                        onChange={(event) => {
                                            UpdateDefaultName(event.target.value);
                                            formik.setFieldValue('default_language', event.target.value);
                                        }}
                                        exclusive
                                        sx={{
                                            minHeight: 48,
                                            padding: '4px',
                                            borderRadius: '12px',
                                            width: "100%",
                                        }}
                                    >
                                        {Object.keys(radioButtonMap).map((key, index) => (
                                            <ToggleButton
                                                key={key}
                                                value={key}
                                                disableIcon
                                                variant="plain"
                                                sx={{ px: 2, alignItems: 'center', width: '25%', p: 1.5, textAlign: 'center'}}
                                            >{radioButtonMap[key]}</ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                }
                            </Box>
                        </Grid>
                        <Grid
                            mt={3}
                            item
                            xs={12}
                            justifyContent={'left'}
                        >
                            <Typography
                                backgroundColor="#7e7e7e"
                                color="white"
                                fontWeight="bold"
                                p={2}
                            >
                                {CONSTANTS.REQUEST_TOPIC.FIELDS.OTHER[lang]}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={4}
                            alignContent={'center'}
                        >   
                            <Typography p={2}>{CONSTANTS.REQUEST_TOPIC.FIELDS.START_DATE[lang]}</Typography>
                        </Grid>
                        <Grid
                            item
                            xs={8}
                            sm={4}
                            alignContent={'center'}
                        >   
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker 
                                    format="DD/MM/YYYY"
                                    onChange={(e) => {
                                        formik.values.start_date = e
                                    }}
                                    value={formik.values.start_date}
                                    sx={{width: '100%'}}
                                />
                            </LocalizationProvider>
                        </Grid>
                        { isMobile? <Grid item xs={4} />: <></> }
                        <Grid
                            item
                            xs={8}
                            sm={4}
                            alignContent={'center'}
                        >   
                            <Stack 
                                direction={"row"} 
                                width={'100%'} 
                                margin={'auto'} 
                                flex={1} 
                                justifyContent="space-evenly"
                            >
                                <Stack alignItems="center">
                                    <Typography variant="body2">{CONSTANTS.REQUEST_TOPIC.FIELDS.YEAR[lang]}</Typography>
                                    <Checkbox 
                                        checked={formik.values.has_year}
                                        onChange={formik.handleChange('has_year')}
                                    />
                                </Stack>
                                <Stack alignItems="center">
                                    <Typography variant="body2">{CONSTANTS.REQUEST_TOPIC.FIELDS.MONTH[lang]}</Typography>
                                    <Checkbox 
                                        checked={formik.values.has_month}
                                        onChange={formik.handleChange('has_month')}
                                    />
                                </Stack>
                                <Stack alignItems="center">
                                    <Typography variant="body2">{CONSTANTS.REQUEST_TOPIC.FIELDS.DAY[lang]}</Typography>
                                    <Checkbox 
                                        checked={formik.values.has_day}
                                        onChange={formik.handleChange('has_day')}
                                    />
                                </Stack>
                            </Stack>
                        </Grid>
                        <Grid
                            item
                            xs={4}
                            alignContent={'center'}
                        >   
                            <Typography p={2}>{CONSTANTS.WOTAGEISHI_DETAILS.FAMOUS_WAZAREN[lang]}</Typography>
                        </Grid>
                        <Grid
                            item
                            xs={8}
                            sm={4}
                            alignContent={'center'}
                        >
                            <Autocomplete
                                multiple
                                id="wazarens-standard"
                                freeSolo
                                filterSelectedOptions
                                options={wazarens}
                                onChange={(e, wazaren) =>updateOrgWazarens(wazaren)}
                                getOptionLabel={wazaren => wazaren.name}
                                renderTags={() => {}}
                                value={orgWazarens}
                                componentsProps={{
                                    paper: {
                                        sx: {
                                            width: '100%'
                                        }
                                    }
                                }}
                                renderInput={params => (
                                <TextField
                                    {...params}
                                    variant="standard"
                                    placeholder=""
                                    margin="normal"
                                    fullWidth
                                    onChange={(e) => {
                                        updateWazarenTextField(e.target.value);
                                    }}
                                />
                                )}
                            />
                        </Grid>
                        {
                            isMobile?
                            <Grid
                                item
                                xs={4}
                                alignContent={'center'}
                            >

                            </Grid>
                            :
                            <>
                            </>
                        }
                        <Grid
                            item
                            xs={8}
                            sm={4}
                            alignContent={'center'}
                        >
                            <Stack 
                                className="selectedTags"
                                direction="row" 
                                spacing={1}
                                useFlexGap
                                flexWrap="wrap"
                            >
                                {
                                    orgWazarens.map((option, index) => {
                                        // This is to handle new options added by the user (allowed by freeSolo prop).
                                        const label = option.name || option;
                                        return (
                                            <Chip 
                                                size="medium"
                                                variant="outlined"
                                                key={option}
                                                label={label}
                                                deleteIcon={<RemoveIcon />} 
                                                onDelete={() => {
                                                    updateOrgWazarens(orgWazarens.filter(entry => entry !== option));
                                                }}
                                                sx={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    width: 'fit-content',
                                                    borderRadius: '0.5rem',
                                                    // backgroundColor: '#f0f0f0',
                                                    borderColor: '#ccc',
                                                }}
                                            />
                                        );
                                    })
                                }
                            </Stack>
                        </Grid>
                        <Grid
                            item
                            xs={4}
                            alignContent={'center'}
                        >   
                            <Typography p={2}>{CONSTANTS.WOTAGEISHI_DETAILS.ORGANIZATION[lang]}</Typography>
                        </Grid>
                        <Grid
                            item
                            xs={8}
                            sm={4}
                            alignContent={'center'}
                        >
                            <Autocomplete
                                multiple
                                id="organizations-standard"
                                freeSolo
                                filterSelectedOptions
                                options={organizations}
                                onChange={(e, organization) => updateSelectedOrganizations(organization)}
                                getOptionLabel={organization => organization.name}
                                renderTags={() => {}}
                                value={selectedOrganizations}
                                componentsProps={{
                                    paper: {
                                        sx: {
                                            width: '100%'
                                        }
                                    }
                                }}
                                renderInput={params => (
                                <TextField
                                    {...params}
                                    variant="standard"
                                    placeholder=""
                                    margin="normal"
                                    fullWidth
                                    onChange={(e) => {
                                        updateOrganizationTextField(e.target.value);
                                    }}
                                />
                                )}
                            />
                        </Grid>
                        {
                            isMobile?
                            <Grid
                                item
                                xs={4}
                                alignContent={'center'}
                            >

                            </Grid>
                            :
                            <>
                            </>
                        }
                        <Grid
                            item
                            xs={8}
                            sm={4}
                            alignContent={'center'}
                        >
                            <Stack 
                                className="selectedTags"
                                direction="row" 
                                spacing={1}
                                useFlexGap
                                flexWrap="wrap"
                            >
                                {
                                    selectedOrganizations.map((option, index) => {
                                        // This is to handle new options added by the user (allowed by freeSolo prop).
                                        const label = option.name || option;
                                        return (
                                            <Chip 
                                                size="medium"
                                                variant="outlined"
                                                key={option}
                                                label={label}
                                                deleteIcon={<RemoveIcon />} 
                                                onDelete={() => {
                                                    updateSelectedOrganizations(selectedOrganizations.filter(entry => entry !== option));
                                                }}
                                                sx={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    width: 'fit-content',
                                                    borderRadius: '0.5rem',
                                                    // backgroundColor: '#f0f0f0',
                                                    borderColor: '#ccc',
                                                }}
                                            />
                                        );
                                    })
                                }
                            </Stack>
                        </Grid>
                        <Grid
                            item
                            xs={4}
                            alignContent={'center'}
                        >   
                            <Typography p={2}>{CONSTANTS.WOTAGEISHI_FILTER.COUNTRY[lang]}</Typography>
                        </Grid>
                        <Grid
                            item
                            xs={8}
                            sm={4}
                            alignContent={'center'}
                        >   
                            <Autocomplete
                                multiple
                                id="organizations-standard"
                                freeSolo
                                filterSelectedOptions
                                options={countryList}
                                onChange={(e, newValue) => {updateCountries(newValue)}}
                                getOptionLabel={country => country.label[lang]}
                                renderTags={() => {}}
                                value={countries}
                                componentsProps={{
                                    paper: {
                                        sx: {
                                            width: '100%'
                                        }
                                    }
                                }}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        variant="standard"
                                        placeholder=""
                                        margin="normal"
                                        fullWidth
                                        onChange={(e, newValue) => {setCountryList(filterCountries(CONSTANTS.COUNTRY_LIST, e.target.value))}}
                                    />
                                )}
                            />
                        </Grid>
                        {
                            isMobile?
                            <Grid
                                item
                                xs={4}
                                alignContent={'center'}
                            >

                            </Grid>
                            :
                            <>
                            </>
                        }
                        <Grid
                            item
                            xs={8}
                            sm={4}
                            alignContent={'center'}
                        >   
                            <Stack 
                                className="selectedTags"
                                direction="row" 
                                spacing={1}
                                useFlexGap
                                flexWrap="wrap"
                            >
                                {
                                    countries.map((option, index) => {
                                        // This is to handle new options added by the user (allowed by freeSolo prop).
                                        const label = option.label[lang] || option;
                                        return (
                                            <Chip 
                                                size="medium"
                                                variant="outlined"
                                                key={option.code}
                                                label={label}
                                                deleteIcon={<RemoveIcon />} 
                                                onDelete={() => {
                                                    updateCountries(countries.filter(entry => entry !== option));
                                                }}
                                                sx={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    width: 'fit-content',
                                                    borderRadius: '0.5rem',
                                                    // backgroundColor: '#f0f0f0',
                                                    borderColor: '#ccc',
                                                }}
                                            />
                                        );
                                    })
                                }
                            </Stack>
                        </Grid>
                        <Grid
                            item
                            xs={4}
                            alignContent={'center'}
                        >   
                            <Typography p={2}>{CONSTANTS.WOTAGEISHI_DETAILS.MARKINGS[lang]}</Typography>
                        </Grid>
                        <Grid
                            item
                            xs={8}
                            sm={4}
                            alignContent={'center'}
                        >   
                            <TextField
                                placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.MARKINGS[lang]}
                                value={marking}
                                onChange={(e) => {
                                    setMarking(e.target.value);
                                }}
                                sx={{
                                    width: '100%'
                                }}
                                InputProps={{
                                    endAdornment: 
                                    <Button 
                                        variant="contained"
                                        onClick={() => {
                                            if (marking !== '') updateMarkings([...markings, marking]);
                                        }}
                                        sx={{
                                            height: '100%'
                                        }}
                                    >
                                        <SendIcon />
                                    </Button>
                                }}
                            /> 
                        </Grid>
                        {
                            isMobile?
                            <Grid
                                item
                                xs={4}
                                alignContent={'center'}
                            >

                            </Grid>
                            :
                            <>
                            </>
                        }
                        <Grid
                            item
                            xs={8}
                            sm={4}
                            alignContent={'center'}
                        >   
                            <Stack 
                                className="selectedTags"
                                direction="row" 
                                spacing={1}
                                useFlexGap
                                flexWrap="wrap"
                            >
                                {
                                    markings.map((option, index) => {
                                        // This is to handle new options added by the user (allowed by freeSolo prop).
                                        const label = option || option;
                                        return (
                                            <Chip 
                                                size="medium"
                                                variant="outlined"
                                                key={option}
                                                label={label}
                                                deleteIcon={<RemoveIcon />} 
                                                onDelete={() => {
                                                    updateMarkings(markings.filter(entry => entry !== option));
                                                }}
                                                sx={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    width: 'fit-content',
                                                    borderRadius: '0.5rem',
                                                    // backgroundColor: '#f0f0f0',
                                                    borderColor: '#ccc',
                                                }}
                                            />
                                        );
                                    })
                                }
                            </Stack>
                        </Grid>
                        <Grid
                            mt={3}
                            item
                            xs={12}
                            justifyContent={'left'}
                        >
                            <Typography
                                backgroundColor="#7e7e7e"
                                color="white"
                                fontWeight="bold"
                                p={2}
                            >
                                {CONSTANTS.WOTAGEISHI_DETAILS.SOCIAL_MEDIA[lang]}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={4}
                            alignContent={'center'}
                        >   
                            <Typography p={2}>
                                YouTube
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={8}
                            alignContent={'center'}
                            pr={2}
                        >   
                            
                            <TextField
                                name="YouTube"
                                variant="outlined"
                                placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.VIDEO_LINK.YOUTUBE[lang]}
                                type="text"
                                fullWidth
                                size="small"
                                error={Boolean(formik.touched.official_account_youtube && formik.errors.official_account_youtube)}
                                onChange={formik.handleChange('official_account_youtube')}
                                value={formik.values.official_account_youtube}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={4}
                            alignContent={'center'}
                        >   
                            <Typography p={2}>
                                Bilibili
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={8}
                            alignContent={'center'}
                            pr={2}
                        >   
                            
                            <TextField
                                name="Bilibili"
                                variant="outlined"
                                placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.VIDEO_LINK.BILIBILI[lang]}
                                type="text"
                                fullWidth
                                size="small"
                                error={Boolean(formik.touched.official_account_bilibili && formik.errors.official_account_bilibili)}
                                onChange={formik.handleChange('official_account_bilibili')}
                                value={formik.values.official_account_bilibili}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={4}
                            alignContent={'center'}
                        >   
                            <Typography p={2}>
                                X
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={8}
                            alignContent={'center'}
                            pr={2}
                        >   
                            
                            <TextField
                                name="X"
                                variant="outlined"
                                placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.VIDEO_LINK.X[lang]}
                                type="text"
                                fullWidth
                                size="small"
                                error={Boolean(formik.touched.official_account_x && formik.errors.official_account_x)}
                                onChange={formik.handleChange('official_account_x')}
                                value={formik.values.official_account_x}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Box>
    );
};
 
export default WotageishiApprovePreview;